/**
 * Due-date arithmetic for IRS notices — the ONE place the frontend does this.
 *
 * ## Why this exists at all
 *
 * `GET /api/irs-notices/{id}` ships a server-computed `daysUntilDue`, but the QUEUE endpoint
 * (`GET /api/irs-notices`) ships raw `responseDueDate` strings and nothing derived. The queue is
 * the screen that has to sort and colour by urgency, so the arithmetic has to happen here.
 *
 * That makes this a **deliberately duplicated invariant**, named rather than hidden — the same
 * choice `irsNoticeClosedStatuses` in `types/index.ts` makes, and for the same reason. The server
 * is authoritative; this mirrors it. Two rules matter and both are pinned by tests:
 *
 * 1. **Date boundaries, not elapsed hours.** The server computes `(due.Date - asOf.Date).TotalDays`,
 *    so a notice due tomorrow does not read as 0 days at 23:00.
 * 2. **UTC, matching `DateTime.UtcNow.Date`.** Using local date parts would let a reader in a
 *    negative-UTC-offset zone see a row marked overdue that the server never counted in the
 *    overdue stat card. Agreeing with the server is worth more than agreeing with a wall clock.
 *
 * ⚠ If the queue endpoint ever starts shipping the derived value, DELETE this module's arithmetic
 * and read the server's field — do not keep both.
 *
 * ## The three facts this module refuses to collapse
 *
 * `none` (no deadline recorded), `unreadable` (a non-empty value that will not parse — a defect),
 * and `ok` (a real day count, which may legitimately be 0 or negative). A missing deadline and a
 * deadline of today are opposite facts; rendering either as the other turns a blank into an
 * emergency or an emergency into a blank.
 */

/** Server threshold: `DueSoon = open.Count(n => DaysUntilDue(...) is >= 0 and <= 14)`. */
export const DUE_SOON_DAYS = 14;

export type DueParse =
  | { kind: 'none' }
  | { kind: 'unreadable' }
  | { kind: 'ok'; days: number };

export type NoticeDueBucket = 'none' | 'overdue' | 'due_soon' | 'later';

const MS_PER_DAY = 86_400_000;

/** Midnight UTC of the calendar day a moment falls on — the JS equivalent of C#'s `.Date`. */
function utcDayStart(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Classify a raw `responseDueDate` against a reference instant.
 *
 * `asOf` is a required parameter rather than a `new Date()` default so every caller is explicit and
 * every test is deterministic.
 */
export function parseDue(
  responseDueDate: string | null | undefined,
  asOf: Date
): DueParse {
  if (!responseDueDate) return { kind: 'none' };

  const due = new Date(responseDueDate);
  if (Number.isNaN(due.getTime())) return { kind: 'unreadable' };

  return { kind: 'ok', days: (utcDayStart(due) - utcDayStart(asOf)) / MS_PER_DAY };
}

/**
 * Whole days until the response is due; negative when overdue; **null when there is no usable
 * date**. Null is not zero — see the module comment.
 */
export function daysUntilDue(
  responseDueDate: string | null | undefined,
  asOf: Date
): number | null {
  const parsed = parseDue(responseDueDate, asOf);
  return parsed.kind === 'ok' ? parsed.days : null;
}

/**
 * Urgency bucket, mirroring the server's stat-card arithmetic exactly.
 *
 * A null day count buckets as `none`: on the server, both `is < 0` and `is >= 0 and <= 14` fail
 * against a null `int?`, so a notice with no deadline is counted in neither card. The UI agrees.
 *
 * ⚠ Open/closed is NOT considered here. The server computes both stats over OPEN notices only; the
 * caller applies that filter, so this stays a pure function of the date.
 */
export function dueBucket(days: number | null): NoticeDueBucket {
  if (days === null) return 'none';
  if (days < 0) return 'overdue';
  if (days <= DUE_SOON_DAYS) return 'due_soon';
  return 'later';
}

/**
 * Format a notice's DATE-ONLY field (received date, response due date) on its **UTC** calendar day.
 *
 * ⚠ Do not replace this with date-fns `format(new Date(iso), 'MMM d, yyyy')`. That renders in the
 * reader's LOCAL zone, so a value stored at UTC midnight — which is what a date picker produces —
 * displays as the PREVIOUS DAY everywhere west of Greenwich. Two consequences, and the second is
 * the serious one:
 *
 *   1. A notice received "Aug 1" reads as "Jul 31" to every US user.
 *   2. It would contradict `daysUntilDue` in the same panel, which is UTC-based to match the server.
 *      "Due in 3 days" beside a date one day earlier than the server's is worse than either alone.
 *
 * `Intl` with an explicit `timeZone` is TZ-independent by construction — unlike the local-parts
 * arithmetic above, this cannot be defeated by the ambient zone, so its test needs no skip guard.
 *
 * ⚠ This is for date-only fields ONLY. A true timestamp (a status change, an audit time) should be
 * rendered in local time, because "when did someone do this" is a wall-clock question for the reader.
 */
const utcDayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatNoticeDate(
  isoDate: string | null | undefined,
  fallback = 'Not recorded'
): string {
  if (!isoDate) return fallback;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return 'Unreadable date';
  return utcDayFormatter.format(d);
}

/** Human label for a due date. Each of the three facts gets its own sentence. */
export function formatDueLabel(
  responseDueDate: string | null | undefined,
  asOf: Date
): string {
  const parsed = parseDue(responseDueDate, asOf);

  if (parsed.kind === 'none') return 'No deadline recorded';
  if (parsed.kind === 'unreadable') return 'Due date unreadable';

  const { days } = parsed;
  if (days === 0) return 'Due today';
  if (days < 0) {
    const overdueBy = Math.abs(days);
    return `${overdueBy} ${overdueBy === 1 ? 'day' : 'days'} overdue`;
  }
  return `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
}
